/* ──────────────────────────────────────────────────────────────────────
 *  TeamMemberEditor — Shared inline editor for team member entries
 *
 *  Used across Chronicles (Team Credits), Communities (Key Leaders),
 *  and Projects (Role Assignments) for consistent team member editing.
 *
 *  Features:
 *    - Name + role title text fields (required / optional)
 *    - Photo upload with square crop (200px) to MinIO
 *    - External profile URL field
 *    - L4E account link/unlink (swap-in / swap-out)
 *    - Remove member action
 *
 *  The component is a controlled editor row — the parent owns the data
 *  and passes onChange / onRemove callbacks.
 * ────────────────────────────────────────────────────────────────────── */

import React, { useRef, useCallback } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import CloseButton from '../CloseButton/CloseButton';
import ContactPicker from '../ContactPicker/ContactPicker';
import type { ContactSearchResult } from '../../api/chat';
import { uploadDataUrl, fileUrl } from '../../api/fileStorage';
import { mediaSrc } from '../../api/mediaUrl';
import styles from './TeamMemberEditor.module.css';

/* ── Shared data shape ────────────────────────────────────────────── */

export interface TeamMemberData {
  /** Client-side ID for keying / tracking (not persisted) */
  id: string;
  /** Display name (manual entry) */
  personName: string;
  /** Role or title */
  roleTitle: string;
  /** Optional "why this person/org matters" statement (Chronicle Leading
   *  Characters — DEV-1352). Only surfaced when showDescription is set. */
  description?: string;
  /** Photo URL (after upload) */
  photoUrl: string;
  /** External profile link (LinkedIn, personal site, etc.) */
  externalProfileUrl: string;
  /** L4E user GUID — when set, member is linked to a platform user */
  linkedUserGuid: string | null;
  /** Display name resolved from the linked L4E user (read-only) */
  linkedUserDisplayName: string | null;
  /** Sort order for persistence */
  sortOrder: number;
}

/** Create a blank team member with a unique client ID */
export function createBlankTeamMember(sortOrder: number): TeamMemberData {
  return {
    id: `tm-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    personName: '',
    roleTitle: '',
    description: '',
    photoUrl: '',
    externalProfileUrl: '',
    linkedUserGuid: null,
    linkedUserDisplayName: null,
    sortOrder,
  };
}

/* ── Props ────────────────────────────────────────────────────────── */

export interface TeamMemberEditorProps {
  /** The member data to edit */
  member: TeamMemberData;
  /** Called when any field changes */
  onChange: (id: string, field: keyof TeamMemberData, value: string | number | null) => void;
  /** Called when the user clicks Remove */
  onRemove: (id: string) => void;
  /**
   * MinIO upload category (e.g., 'team-photos', 'enabler-photos').
   * Determines the storage bucket path.
   */
  uploadCategory?: string;
  /**
   * Entity GUID passed to the upload function for path namespacing.
   * Optional — falls back to the member's id.
   */
  uploadEntityId?: string;
  /** If true, photo upload is disabled (e.g., for linked L4E users who have their own photo) */
  photoReadOnly?: boolean;
  /** Whether L4E link/unlink buttons are shown. Default true. */
  showLinkActions?: boolean;
  /** Optional: override index label (e.g., "Member 1") */
  indexLabel?: string;
  /** Show the optional "why this matters" description textarea (Chronicle
   *  Leading Characters — DEV-1352). Default false. */
  showDescription?: boolean;
  /** Label for the description field (only used when showDescription). */
  descriptionLabel?: string;
  /** Placeholder for the description field (only used when showDescription). */
  descriptionPlaceholder?: string;
}

/* ── Constants ────────────────────────────────────────────────────── */

const PHOTO_MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const PHOTO_SIZE = 200; // Square crop target

/* ── Component ────────────────────────────────────────────────────── */

export default function TeamMemberEditor({
  member,
  onChange,
  onRemove,
  uploadCategory = 'team-photos',
  uploadEntityId,
  photoReadOnly = false,
  showLinkActions = true,
  indexLabel,
  showDescription = false,
  descriptionLabel,
  descriptionPlaceholder,
}: TeamMemberEditorProps) {
  const { t } = useAppTranslation('common');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isLinked = !!member.linkedUserGuid;
  const displayName = isLinked && member.linkedUserDisplayName
    ? member.linkedUserDisplayName
    : member.personName;

  /* ---- Field change shortcut ---- */
  const set = useCallback(
    (field: keyof TeamMemberData, value: string | number | null) => {
      onChange(member.id, field, value);
    },
    [onChange, member.id]
  );

  /* ---- Photo upload with square crop ---- */
  const handlePhotoSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > PHOTO_MAX_BYTES) return; // Silently reject oversize

      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = PHOTO_SIZE;
          canvas.height = PHOTO_SIZE;
          const ctx = canvas.getContext('2d');
          if (!ctx) return;

          // Centre-crop to square
          const side = Math.min(img.width, img.height);
          const sx = (img.width - side) / 2;
          const sy = (img.height - side) / 2;
          ctx.drawImage(img, sx, sy, side, side, 0, 0, PHOTO_SIZE, PHOTO_SIZE);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.85);

          // Upload to MinIO
          const entityId = uploadEntityId || member.id;
          const fileName = `team-${entityId}-${Date.now()}.jpg`;
          uploadDataUrl(dataUrl, uploadCategory, entityId, fileName)
            .then((result) => {
              const url = `${fileUrl(result.key)}?v=${Date.now()}`;
              set('photoUrl', url);
            })
            .catch(() => {
              // Fallback: store data URL directly (dev / offline)
              set('photoUrl', dataUrl);
            });
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);

      // Reset so the same file can be re-selected
      e.target.value = '';
    },
    [set, uploadCategory, uploadEntityId, member.id]
  );

  /* ---- L4E swap-out (unlink) ---- */
  const handleUnlink = useCallback(() => {
    set('linkedUserGuid', null);
    set('linkedUserDisplayName', null);
    // Keep manual fields so the entry isn't blank after unlinking
  }, [set]);

  /* ---- L4E swap-in (link via contact picker) ---- */
  const handleLink = useCallback((contact: ContactSearchResult) => {
    set('linkedUserGuid', contact.userGuid);
    set('linkedUserDisplayName', contact.displayName);
    // Backfill manual name so it persists if later unlinked
    if (!member.personName) {
      set('personName', contact.displayName);
    }
    // Backfill photo if the contact has one and member doesn't
    if (!member.photoUrl && contact.profileImageUrl) {
      set('photoUrl', contact.profileImageUrl);
    }
  }, [set, member.personName, member.photoUrl]);

  /* ---- Render ---- */
  return (
    <div className={styles.card}>
      {/* ── Row header: index label + remove ── */}
      <div className={styles.cardHeader}>
        {indexLabel && <span className={styles.indexLabel}>{indexLabel}</span>}
        <CloseButton
          onClick={() => onRemove(member.id)}
          size="sm"
          variant="danger"
          title={t('teamMemberEditor.remove')}
        />
      </div>

      {/* ── Photo + fields side by side ── */}
      <div className={styles.cardBody}>
        {/* Photo column */}
        <div className={styles.photoCol}>
          {member.photoUrl ? (
            <img
              className={styles.photoPreview}
              src={mediaSrc(member.photoUrl) ?? undefined}
              alt={displayName}
            />
          ) : (
            <div className={styles.photoPlaceholder}>
              {displayName ? displayName.charAt(0).toUpperCase() : '?'}
            </div>
          )}

          {!photoReadOnly && (
            <div className={styles.photoActions}>
              <label className={styles.uploadBtn}>
                {member.photoUrl
                  ? t('teamMemberEditor.changePhoto')
                  : t('teamMemberEditor.uploadPhoto')}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handlePhotoSelect}
                />
              </label>
              {member.photoUrl && (
                <button
                  type="button"
                  className={styles.removePhotoBtn}
                  onClick={() => set('photoUrl', '')}
                >
                  {t('teamMemberEditor.removePhoto')}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Fields column */}
        <div className={styles.fieldsCol}>
          {/* Name */}
          <label className={styles.fieldLabel}>
            {t('teamMemberEditor.name')}
            <input
              type="text"
              className={styles.input}
              value={member.personName}
              onChange={(e) => set('personName', e.target.value)}
              placeholder={t('teamMemberEditor.namePlaceholder')}
              disabled={isLinked}
            />
          </label>

          {/* Role title */}
          <label className={styles.fieldLabel}>
            {t('teamMemberEditor.role')}
            <input
              type="text"
              className={styles.input}
              value={member.roleTitle}
              onChange={(e) => set('roleTitle', e.target.value)}
              placeholder={t('teamMemberEditor.rolePlaceholder')}
            />
          </label>

          {/* Optional "why this matters" description (Chronicle Leading
              Characters — DEV-1352). Gated so other consumers are unchanged. */}
          {showDescription && (
            <label className={styles.fieldLabel}>
              {descriptionLabel ?? t('teamMemberEditor.description')}
              <textarea
                className={styles.input}
                style={{ resize: 'vertical', minBlockSize: 64 }}
                rows={3}
                value={member.description ?? ''}
                onChange={(e) => set('description', e.target.value)}
                placeholder={descriptionPlaceholder ?? t('teamMemberEditor.descriptionPlaceholder')}
              />
            </label>
          )}

          {/* External profile URL */}
          <label className={styles.fieldLabel}>
            {t('teamMemberEditor.externalUrl')}
            <input
              type="url"
              className={styles.input}
              value={member.externalProfileUrl}
              onChange={(e) => set('externalProfileUrl', e.target.value)}
              placeholder={t('teamMemberEditor.externalUrlPlaceholder')}
            />
          </label>

          {/* L4E link status + actions */}
          {showLinkActions && (
            <div className={styles.linkSection}>
              {isLinked ? (
                <div className={styles.linkRow}>
                  <span className={styles.linkedIndicator}>
                    <span className={styles.linkedDot} />
                    {t('teamMemberEditor.linkedTo', { name: member.linkedUserDisplayName || '' })}
                  </span>
                  <button
                    type="button"
                    className={styles.unlinkBtn}
                    onClick={handleUnlink}
                  >
                    {t('teamMemberEditor.unlink')}
                  </button>
                </div>
              ) : (
                <label className={styles.fieldLabel}>
                  {t('teamMemberEditor.linkAccount')}
                  <ContactPicker
                    onSelect={handleLink}
                    excludeGuids={member.linkedUserGuid ? [member.linkedUserGuid] : []}
                    placeholder={t('contactPicker.placeholder')}
                    autoFocus={false}
                  />
                </label>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
